import { HoverActionButton, HoverActionButtonHierarchy, HoverActionButtonSize } from '@vizit/atoms';
import { HeartFilledIcon, HeartIcon, Trash01Icon, XCloseIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import React from 'react';
import { AssetContext } from './asset.context-provider';
import { BUTTON_TOP_INSET } from './constants';

/**
 * The surfaces an asset tile can appear on. Each variant carries its own
 * ordered set of hover actions (see `VARIANT_ACTIONS`).
 */
enum AssetTileVariant {
  /**
   * Spark images tab on the asset details page: favorite the image and delete
   * the image. Pressing the tile itself navigates to the image's own details
   * page, so there is no jump-to action here.
   */
  SparkTab = 'spark-tab',
  /**
   * Asset grids that favorite and delete an image: the product details page's
   * assets drawer, and the collection details grid.
   */
  Sidebar = 'sidebar',
  /**
   * Carousel grid on the product details page: remove the image from the
   * carousel without deleting it.
   */
  Carousel = 'carousel',
}

/**
 * A single hover action an asset tile variant can surface.
 */
enum AssetTileAction {
  /** Toggle the image's workspace-wide favorite flag. */
  Favorite = 'favorite',
  /** Permanently delete the image itself. Destructive. */
  Delete = 'delete',
  /** Remove the image from the carousel; the image is not deleted. */
  Remove = 'remove',
}

interface AssetActionsProps {
  /**
   * Which asset tile variant to render the hover actions for. Decides the
   * action set and its order.
   *
   * @required
   * @type AssetTileVariant
   */
  variant: AssetTileVariant;
  /**
   * Handler for the "Favorite image" toggle. The action is only rendered on
   * variants that include it and when this handler is provided. Called with
   * no arguments; the caller decides the desired end state from `isFavorite`.
   *
   * @optional
   * @type () => void
   */
  onFavorite?: () => void;
  /**
   * Whether the asset is currently favorited. Renders the heart filled.
   *
   * @optional
   * @default false
   * @type boolean
   */
  isFavorite?: boolean;
  /**
   * Whether the favorite toggle is unavailable to this user. Renders the
   * button in its disabled state rather than hiding it, so the action's
   * absence is legible instead of silent.
   *
   * @optional
   * @default false
   * @type boolean
   */
  isFavoriteDisabled?: boolean;
  /**
   * Whether a favorite toggle request is in flight for this asset. Shows the
   * favorite button in its loading state.
   *
   * @optional
   * @default false
   * @type boolean
   */
  isFavoritePending?: boolean;
  /**
   * Handler for the "Delete image" action. The action is only rendered on
   * variants that include it and when this handler is provided.
   *
   * @optional
   * @type () => void
   */
  onDelete?: () => void;
  /**
   * Handler for the "Remove image from carousel" action. The action is only
   * rendered on variants that include it and when this handler is provided.
   *
   * @optional
   * @type () => void
   */
  onRemove?: () => void;
  /**
   * Pass-through class for the actions container.
   *
   * @optional
   * @type string
   */
  className?: string;
}

/**
 * Ordered hover actions per tile variant, top to bottom.
 */
const VARIANT_ACTIONS: Record<AssetTileVariant, AssetTileAction[]> = {
  [AssetTileVariant.SparkTab]: [AssetTileAction.Favorite, AssetTileAction.Delete],
  [AssetTileVariant.Sidebar]: [AssetTileAction.Favorite, AssetTileAction.Delete],
  [AssetTileVariant.Carousel]: [AssetTileAction.Remove],
};

interface AssetTileActionConfig {
  icon: React.ReactNode;
  label: string;
  hierarchy: HoverActionButtonHierarchy;
}

const ACTION_CONFIG: Partial<Record<AssetTileAction, AssetTileActionConfig>> = {
  [AssetTileAction.Favorite]: {
    // The filled heart is swapped in at render time from `isFavorite`.
    icon: <HeartIcon size={20} />,
    label: 'Favorite image',
    hierarchy: HoverActionButtonHierarchy.Secondary,
  },
  [AssetTileAction.Delete]: {
    icon: <Trash01Icon size={20} />,
    label: 'Delete image',
    hierarchy: HoverActionButtonHierarchy.Destructive,
  },
  [AssetTileAction.Remove]: {
    icon: <XCloseIcon size={20} />,
    label: 'Remove image from carousel',
    hierarchy: HoverActionButtonHierarchy.Destructive,
  },
};

const actionVisibilityClasses = cva({
  // Keyboard users reach the buttons without hovering; reveal the focused one
  // so it is never an invisible tab stop (the tile's focus-within also flips
  // keyboard focus-visible, revealing the whole stack without letting a
  // mouse click pin it open after the pointer leaves).
  // Touch has neither hover nor focus-visible, so coarse pointers keep the
  // actions permanently visible and tappable instead of unreachable.
  base: [
    'focus-visible:opacity-100 focus-visible:pointer-events-auto',
    '[@media(pointer:coarse)]:opacity-100 [@media(pointer:coarse)]:pointer-events-auto',
  ],
  variants: {
    isVisible: {
      true: 'opacity-100 pointer-events-auto',
      // `pointer-events-none` keeps the invisible buttons out of hit testing:
      // opacity alone would let a touch user trigger an unseen destructive
      // action. Keyboard focus is unaffected by pointer-events.
      false: 'opacity-0 pointer-events-none',
    },
  },
  defaultVariants: {
    isVisible: false,
  },
});

/**
 * `Asset.Actions` renders the hover action buttons of an asset tile as a
 * vertical stack anchored to the tile's top-right corner, matching the top
 * offset the tile computes for its other hover controls. The `variant` prop
 * decides which actions appear; each action only renders when its handler is
 * provided. The stack is visible while the tile is hovered or focused.
 */
function AssetActions(props: AssetActionsProps) {
  const { variant, onFavorite, isFavorite = false, isFavoritePending = false } = props;
  const { isFavoriteDisabled = false } = props;
  const { onDelete, onRemove, className } = props;
  const { buttonTopOffset, isHovered, isFocusVisible, isViewOnly } = React.useContext(AssetContext);

  if (isViewOnly) return null;

  const handlers: Partial<Record<AssetTileAction, () => void>> = {
    [AssetTileAction.Favorite]: onFavorite,
    [AssetTileAction.Delete]: onDelete,
    [AssetTileAction.Remove]: onRemove,
  };

  const actions = VARIANT_ACTIONS[variant].flatMap((action) => {
    const config = ACTION_CONFIG[action];
    const onPress = handlers[action];
    return config && onPress ? [{ action, config, onPress }] : [];
  });

  if (actions.length === 0) return null;

  return (
    <div
      className={cx('absolute right-1 flex flex-col gap-1', className)}
      style={{ top: buttonTopOffset ?? `var(--asset-button-top, ${BUTTON_TOP_INSET}px)` }}
    >
      {actions.map(({ action, config, onPress }) => {
        const isFavoriteAction = action === AssetTileAction.Favorite;
        const isVisible = isHovered || isFocusVisible;
        return (
          <HoverActionButton
            key={action}
            aria-label={config.label}
            aria-pressed={isFavoriteAction ? isFavorite : undefined}
            hierarchy={config.hierarchy}
            size={HoverActionButtonSize.Sm}
            icon={isFavoriteAction && isFavorite ? <HeartFilledIcon size={20} /> : config.icon}
            isDisabled={isFavoriteAction && isFavoriteDisabled}
            isLoading={isFavoriteAction && isFavoritePending}
            onPress={onPress}
            className={cx('h-9 w-9 sm:h-18 sm:w-18', actionVisibilityClasses({ isVisible }))}
          />
        );
      })}
    </div>
  );
}

export { AssetActions, AssetTileAction, AssetTileVariant };
export type { AssetActionsProps };
